import { EditMealRequest } from "@/clients/models/EditMealRequest";
import { MealsService } from "@/clients/services/MealsService";
import { QueryKeys } from "@/constants/query-keys";
import {
  planDetailMealQueryKey,
  setPlanDetailMealData,
} from "@/features/meal-plan/hooks/use-meal-detail-data";
import { MealPlan } from "@/pages/plans/types/meal-plan.type";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export interface EditMealNutritionParams {
  mealId: string;
  requestBody: EditMealRequest;
  updateMealDetail?: boolean;
}

export const useEditMealNutrition = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: [QueryKeys.MY_FOODS, "edit-nutrition"],
    mutationFn: async ({ mealId, requestBody }: EditMealNutritionParams) => {
      const response = await MealsService.editMealApiV1MealsMealIdPatch(mealId, requestBody);
      return response.data;
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MY_FOODS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.YOUR_FOOD] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.FAVORITE_MEALS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SCALED_SERVINGS] });

      if (variables.updateMealDetail === false) return;

      const currentDetail = queryClient.getQueryData<MealPlan | null>(planDetailMealQueryKey);
      const currentMealId = currentDetail?.meal?.id;
      if (
        !currentDetail ||
        (currentMealId !== result.requested_meal_id && currentMealId !== result.resolved_meal_id)
      ) {
        return;
      }

      setPlanDetailMealData(queryClient, {
        ...currentDetail,
        meal: result.meal,
      });
    },
  });

  return {
    editMealNutritionAsync: mutation.mutateAsync,
    isEditingMealNutrition: mutation.isPending,
    error: mutation.error,
  };
};
